import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { ArrowLeft, Play, Pause, Volume2, Loader2, Check } from 'lucide-react';
import { LanguageSelector } from '../components/LanguageSelector';
import { MyErotikaLogo } from '../components/MyErotikaLogo';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SelectVoicePage() {
  const navigate = useNavigate();
  const { gender, characterId } = useParams();
  const { user, token, logout, isPremium } = useAuth();
  const { t, language } = useLanguage();
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [playingVoice, setPlayingVoice] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    const fetchVoices = async () => {
      try {
        const response = await axios.get(`${API}/voices/${gender}?language=${language}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setVoices(response.data);
        if (response.data.length > 0) {
          setSelectedVoice(response.data[0].id);
        }
      } catch (error) {
        console.error('Error fetching voices:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchVoices();
  }, [gender, language, token]);

  const playPreview = async (voice) => {
    // Stop current audio if playing
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    if (playingVoice === voice.id) {
      setPlayingVoice(null);
      return;
    }

    setLoadingPreview(voice.id);
    
    try {
      const response = await axios.post(`${API}/tts/preview`, {
        text: voice.preview_text,
        voice_id: voice.id,
        gender: gender
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.data.audio_url) {
        toast.info(language === 'fr' ? 'Aperçu vocal temporairement indisponible' : 'Voice preview temporarily unavailable');
        setLoadingPreview(null);
        return;
      }

      const audio = new Audio(response.data.audio_url);
      audioRef.current = audio;
      
      audio.onended = () => {
        setPlayingVoice(null);
      };
      
      audio.onerror = () => {
        toast.error(language === 'fr' ? 'Erreur de lecture audio' : 'Audio playback error');
        setPlayingVoice(null);
      };

      await audio.play();
      setPlayingVoice(voice.id);
    } catch (error) {
      console.error('Error playing preview:', error);
      toast.info(language === 'fr' ? 'Aperçu vocal temporairement indisponible' : 'Voice preview temporarily unavailable');
    } finally {
      setLoadingPreview(null);
    }
  };

  const handleContinue = () => {
    if (selectedVoice) {
      navigate(`/select-level/${gender}/${characterId}/${selectedVoice}`);
    }
  };

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-velvet-bg flex items-center justify-center">
        <Loader2 className="animate-spin text-purple-main" size={40} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-velvet-bg">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate(`/select-character/${gender}`)}
            className="flex items-center gap-2 text-velvet_text-secondary hover:text-velvet_text-accent transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft size={20} />
            <MyErotikaLogo size="sm" />
          </button>
          <div className="flex items-center gap-4">
            <LanguageSelector />
            <div className="flex items-center gap-3">
              <span className="text-velvet_text-secondary text-sm">
                {user?.username}
                {isPremium && (
                  <span className="ml-2 px-2 py-0.5 bg-premium/20 text-premium text-xs rounded-full">
                    Premium
                  </span>
                )}
              </span>
              <Button 
                variant="ghost" 
                onClick={logout}
                className="text-velvet_text-secondary hover:text-velvet_text-primary text-sm"
                data-testid="logout-button"
              >
                {t('logout')}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-32 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-4">
            <span className="text-velvet_text-accent text-sm font-medium tracking-wider uppercase">
              {({fr: 'Étape 3 sur 4', en: 'Step 3 of 4', es: 'Paso 3 de 4', de: 'Schritt 3 von 4', pt: 'Passo 3 de 4'})[language] || 'Step 3 of 4'}
            </span>
          </div>
          <h1 className="font-heading text-4xl lg:text-5xl text-center gradient-text mb-4 animate-slideUp">
            {({fr: 'Choisissez la voix', en: 'Choose the voice', es: 'Elige la voz', de: 'Wähle die Stimme', pt: 'Escolhe a voz'})[language] || 'Choose the voice'}
          </h1>
          <p className="text-velvet_text-secondary text-center mb-16 max-w-xl mx-auto">
            {language === 'fr' 
              ? 'Écoutez les différentes voix et sélectionnez celle qui vous plaît'
              : 'Listen to different voices and select the one you like'
            }
          </p>

          <div className="space-y-4">
            {voices.map((voice, index) => (
              <button
                key={voice.id}
                onClick={() => setSelectedVoice(voice.id)}
                className={`w-full group relative glass rounded-xl p-6 text-left transition-all animate-slideUp stagger-${index + 1} ${
                  selectedVoice === voice.id 
                    ? 'border-2 border-purple-main/50 bg-purple-main/10' 
                    : 'border-2 border-transparent hover:border-white/10'
                }`}
                data-testid={`voice-card-${voice.id}`}
              >
                <div className="flex items-center gap-4">
                  {/* Play button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      playPreview(voice);
                    }}
                    className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${
                      playingVoice === voice.id 
                        ? 'bg-purple-main text-white' 
                        : 'bg-purple-main/20 text-purple-light hover:bg-purple-main/30'
                    }`}
                    disabled={loadingPreview === voice.id}
                    data-testid={`play-voice-${voice.id}`}
                  >
                    {loadingPreview === voice.id ? (
                      <Loader2 className="animate-spin" size={24} />
                    ) : playingVoice === voice.id ? (
                      <Pause size={24} />
                    ) : (
                      <Play size={24} className="ml-1" />
                    )}
                  </button>

                  {/* Voice info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Volume2 className="text-purple-light" size={18} />
                      <h3 className="font-heading text-xl text-velvet_text-primary">
                        {voice.name}
                      </h3>
                    </div>
                    <p className="text-velvet_text-secondary text-sm">
                      {voice.description}
                    </p>
                    <p className="text-velvet_text-accent text-sm mt-2 italic">
                      "{voice.preview_text}"
                    </p>
                  </div>

                  {/* Selection indicator */}
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                    selectedVoice === voice.id 
                      ? 'bg-purple-main text-white' 
                      : 'bg-white/5'
                  }`}>
                    {selectedVoice === voice.id && <Check size={18} />}
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Continue button */}
          <div className="mt-12 text-center animate-slideUp">
            <Button
              onClick={handleContinue}
              disabled={!selectedVoice}
              className="bg-purple-main hover:bg-purple-dark text-white rounded-full px-12 py-6 text-lg font-semibold shadow-neon hover:scale-105 transition-transform"
              data-testid="continue-button"
            >
              {t('continue')}
              <ArrowLeft className="ml-2 rotate-180" size={20} />
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
